import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, RefreshCw, Zap, ShieldCheck, ScrollText, Receipt,
  Circle, Wallet,
} from 'lucide-react';
import { Stamp, Modal, Field, Row } from './SharedUI';
import httpInjectorService from 'services/http-injector.service';
import {
  fmtMoney, fmtDate, fmtDateTime,
} from './subscriptionConstants';

/**
 * Props:
 *  - orgId:  string, the org currently being viewed
 *  - orgs:   array of orgs already loaded by the parent list (used to avoid
 *            a refetch for fields the list already has)
 *  - plans:  array of plans already loaded by the parent (used to populate
 *            the "assign plan" dropdown without a refetch)
 *  - notify: (message, type) => void, toast helper from the parent
 *  - onBack: () => void, go back to the subscriptions list
 */
const OrganizationDetail = ({ orgId, orgs = [], plans = [], notify, onBack }) => {
  const [tab, setTab] = useState('overview');
  const [detail, setDetail] = useState(null);
  const [logs, setLogs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'activate' | 'assign'

  const baseOrg = orgs.find(o => o.org_id === orgId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, l, inv] = await Promise.all([
        httpInjectorService.getSubscriptionByOrg(orgId),
        httpInjectorService.getSubscriptionLogs(orgId),
        httpInjectorService.getInvoices(orgId),
      ]);
      setDetail(d);
      setLogs(l.logs || l.data || []);
      setInvoices(inv.invoices || inv.data || []);
    } catch (e) {
      notify?.(e.message || 'Could not load organization detail', 'error');
    } finally {
      setLoading(false);
    }
  }, [orgId, notify]);

  useEffect(() => { load(); }, [load]);

  const sub = detail?.subscription || baseOrg;

  const handleActivate = async (form) => {
    try {
      await httpInjectorService.activateSubscription({ org_id: orgId, ...form });
      notify?.('Subscription activated and invoice created');
      setModal(null);
      load();
    } catch (e) {
      notify?.(e.message || 'Activation failed', 'error');
    }
  };

  const handleAssign = async (form) => {
    try {
      await httpInjectorService.assignPlan({ org_id: orgId, ...form });
      notify?.(`Plan reassigned to ${form.plan_name}`);
      setModal(null);
      load();
    } catch (e) {
      notify?.(e.message || 'Plan assignment failed', 'error');
    }
  };

  if (!sub) return null;

  return (
    <div>
      <button className="flex items-center gap-1.5 text-sm mb-4" style={{ color: 'var(--ink-dim)' }} onClick={onBack}>
        <ArrowLeft size={14} /> All subscriptions
      </button>

      <div className="sa-panel p-5 mb-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs sa-mono mb-1" style={{ color: 'var(--ink-faint)', letterSpacing: '.08em' }}>{sub.org_id}</div>
            <h2 className="sa-display text-2xl font-semibold" style={{ color: 'var(--ink)' }}>{sub.org_name}</h2>
            <div className="flex items-center gap-3 mt-2">
              <Stamp status={sub.status} />
              <span className="text-sm" style={{ color: 'var(--ink-dim)' }}>on <span style={{ color: 'var(--ink)' }}>{sub.plan_name}</span></span>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="sa-btn sa-btn-ghost px-3.5 py-2 text-sm" onClick={() => setModal('assign')}><RefreshCw size={14} /> Assign plan</button>
            <button className="sa-btn sa-btn-primary px-3.5 py-2 text-sm" onClick={() => setModal('activate')}><Zap size={14} /> Activate</button>
          </div>
        </div>
      </div>

      <div className="flex gap-6 mb-5" style={{ borderBottom: '1px solid var(--rule)' }}>
        {[
          { id: 'overview', label: 'Overview', icon: ShieldCheck },
          { id: 'logs', label: 'Logs', icon: ScrollText },
          { id: 'invoices', label: 'Invoices', icon: Receipt },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`sa-tab flex items-center gap-1.5 pb-3 text-sm font-medium ${tab === t.id ? 'active' : ''}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: 'var(--ink-dim)' }}>Loading…</div>
      ) : tab === 'overview' ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="sa-panel p-5">
            <h4 className="sa-mono text-xs mb-3" style={{ color: 'var(--ink-faint)', letterSpacing: '.06em' }}>SUBSCRIPTION</h4>
            <Row label="Plan" value={sub.plan_name} />
            <Row label="Started" value={fmtDate(sub.started_at)} />
            <Row label="Renews" value={fmtDate(sub.renews_at)} />
            <Row label="MRR" value={fmtMoney(sub.mrr)} mono />
          </div>
          <div className="sa-panel p-5">
            <h4 className="sa-mono text-xs mb-3" style={{ color: 'var(--ink-faint)', letterSpacing: '.06em' }}>TRIAL</h4>
            {detail?.trial ? (
              <>
                <Row label="Started" value={fmtDate(detail.trial.started_at)} />
                <Row label="Ends" value={fmtDate(detail.trial.ends_at)} />
                <Row label="Days left" value={String(detail.trial.days_left)} />
              </>
            ) : <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Not on trial.</p>}
          </div>
          <div className="sa-panel p-5 col-span-2">
            <h4 className="sa-mono text-xs mb-3" style={{ color: 'var(--ink-faint)', letterSpacing: '.06em' }}>ACCESS CACHE</h4>
            <Row label="Last synced" value={fmtDateTime(detail?.access_cache?.last_synced)} />
            <div className="flex flex-wrap gap-2 mt-2">
              {(detail?.access_cache?.features_enabled || []).map(f => (
                <span key={f} className="sa-mono text-xs px-2.5 py-1 rounded" style={{ background: 'rgba(147,51,234,0.08)', color: 'var(--brass)', border: '1px solid rgba(147,51,234,0.25)' }}>{f}</span>
              ))}
            </div>
          </div>
        </div>
      ) : tab === 'logs' ? (
        <div className="sa-panel overflow-hidden">
          {logs.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--ink-dim)' }}>No log entries.</div>
          ) : logs.map((l, i) => (
            <div key={i} className="sa-table-row px-5 py-4 flex items-start gap-4" style={{ cursor: 'default' }}>
              <Circle size={7} className="mt-1.5 flex-shrink-0" fill="var(--brass)" color="var(--brass)" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="sa-mono text-xs font-semibold" style={{ color: 'var(--ink)' }}>{l.action}</span>
                  <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>by {l.actor}</span>
                </div>
                <p className="text-sm mt-0.5" style={{ color: 'var(--ink-dim)' }}>{l.details}</p>
              </div>
              <span className="sa-mono text-xs whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}>{fmtDateTime(l.timestamp)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="sa-panel overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-4 px-5 py-3 text-xs font-semibold sa-mono" style={{ color: 'var(--ink-faint)', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid var(--rule)' }}>
            <span>Invoice</span><span>Date</span><span>Amount</span><span>Status</span>
          </div>
          {invoices.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--ink-dim)' }}>No invoices yet.</div>
          ) : invoices.map((inv) => (
            <div key={inv.invoice_id} className="sa-table-row grid grid-cols-[1fr_1fr_1fr_1fr] gap-4 px-5 py-4 items-center" style={{ cursor: 'default' }}>
              <span className="sa-mono text-sm" style={{ color: 'var(--ink)' }}>{inv.invoice_id}</span>
              <span className="text-sm" style={{ color: 'var(--ink-dim)' }}>{fmtDate(inv.date)}</span>
              <span className="sa-mono text-sm" style={{ color: 'var(--ink)' }}>{fmtMoney(inv.amount)}</span>
              <Wallet size={14} style={{ color: 'var(--ink-faint)' }} className="hidden" />
              <span className="text-sm capitalize" style={{ color: inv.status === 'paid' ? 'var(--sage)' : 'var(--ink-faint)' }}>{inv.status}</span>
            </div>
          ))}
        </div>
      )}

      {modal === 'activate' && (
        <ActivateModal org={sub} onClose={() => setModal(null)} onSubmit={handleActivate} />
      )}
      {modal === 'assign' && (
        <AssignModal org={sub} plans={plans} onClose={() => setModal(null)} onSubmit={handleAssign} />
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────
   Modals scoped to this screen only
   ──────────────────────────────────────────────────────────────── */
function ActivateModal({ org, onClose, onSubmit }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: org.mrr || '', reference: '' });
  return (
    <Modal title={`Activate subscription — ${org.org_name}`} onClose={onClose}>
      <p className="text-sm mb-4" style={{ color: 'var(--ink-dim)' }}>
        Manually activates a pending paid subscription and creates the matching invoice record.
      </p>
      <Field label="Amount charged (USD)">
        <input type="number" className="sa-input w-full px-3 py-2 text-sm sa-mono" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
      </Field>
      <Field label="Payment reference (optional)">
        <input className="sa-input w-full px-3 py-2 text-sm" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="e.g. bank transfer ref, PO number" />
      </Field>
      <div className="flex justify-end gap-2 mt-2">
        <button className="sa-btn sa-btn-ghost px-4 py-2 text-sm" onClick={onClose}>Cancel</button>
        <button className="sa-btn sa-btn-primary px-4 py-2 text-sm" disabled={saving} onClick={async () => { setSaving(true); await onSubmit(form); setSaving(false); }}>
          {saving ? 'Activating…' : 'Activate & invoice'}
        </button>
      </div>
    </Modal>
  );
}

function AssignModal({ org, plans, onClose, onSubmit }) {
  const [saving, setSaving] = useState(false);
  const [planName, setPlanName] = useState(org.plan_name);
  return (
    <Modal title={`Assign plan — ${org.org_name}`} onClose={onClose}>
      <p className="text-sm mb-4" style={{ color: 'var(--ink-dim)' }}>Changes the plan for this organization immediately.</p>
      <Field label="Plan">
        <select className="sa-input w-full px-3 py-2 text-sm" value={planName} onChange={e => setPlanName(e.target.value)}>
          {plans.map(p => <option key={p.id} value={p.name}>{p.name} — {fmtMoney(p.price)}/mo</option>)}
        </select>
      </Field>
      <div className="flex justify-end gap-2 mt-2">
        <button className="sa-btn sa-btn-ghost px-4 py-2 text-sm" onClick={onClose}>Cancel</button>
        <button className="sa-btn sa-btn-primary px-4 py-2 text-sm" disabled={saving} onClick={async () => { setSaving(true); await onSubmit({ plan_name: planName }); setSaving(false); }}>
          {saving ? 'Assigning…' : 'Assign plan'}
        </button>
      </div>
    </Modal>
  );
}

export default OrganizationDetail;
